import React from 'react';

import { useTranslation } from 'react-i18next';

import { AssetDetails, CartPrice, Sheet } from '@pkg/components';
import { Button } from '@pkg/components/Sections/Profile/Buttons';
import { type BaseCurrencyType } from '@pkg/types';

import { CollectionChainType } from '@app/client';

import { mock } from './mock';

import {
    CartItem,
    CartList,
    ClearAction,
    FooterInterior,
    MainInterior,
    SectionHeader,
    SectionHeaderTitle,
    Summary,
    SummaryTitle,
} from './SweepSheet.style';
import { tokens } from './SweepSheet.tokens';

export interface SweepSheetProps {
    onClose: () => void;
}

function chainToSymbol(type?: CollectionChainType): BaseCurrencyType {
    if (type === CollectionChainType.Aptos) return 'apt';
    if (type === CollectionChainType.Ethereum) return 'eth';
    if (type === CollectionChainType.Polygon) return 'matic';
    return 'usd';
}

function stringToFloat(value = '0') {
    return parseFloat(value);
}

export function SweepSheet({ onClose }: SweepSheetProps) {
    const [translate] = useTranslation('marketplace');

    const currency = chainToSymbol(mock.cart[0]?.collection.chain);
    const totalPrice = mock.cart.reduce((sum, { price }) => {
        // TODO: `price` either should not be a `string`,
        // or we should have reliable utilities for parsing big numbers.
        const rawPrice = stringToFloat(price?.value);
        return sum + rawPrice;
    }, 0);

    function handleSubmitOrder() {
        // eslint-disable-next-line no-console
        console.log('TODO: Submit order.');
    }

    function handleClearCart() {
        // eslint-disable-next-line no-console
        console.log('TODO: Clear cart.');
    }

    const cartItems = mock.cart.map(({ name, collection, imageUrl, price }) => (
        <CartItem key={`CartItem-${name}`}>
            <AssetDetails name={name} collectionName={collection.name} imageUrl={imageUrl} />
            <CartPrice currency={price?.currency} price={stringToFloat(price?.value)} size={tokens.summary.priceSize} />
        </CartItem>
    ));

    return (
        <Sheet size="thin" gapless backdrop onClose={onClose}>
            <Sheet.Header largeTitle title={translate('sweep-sheet-title')} onClose={onClose} />

            <Sheet.Main>
                <Sheet.Layout>
                    <Sheet.Layout.Columns stretch>
                        <Sheet.Layout.Columns.Primary padded scrollable>
                            <MainInterior>
                                <SectionHeader>
                                    <SectionHeaderTitle>
                                        {translate('sweep-sheet-count', { count: mock.cart.length })}
                                    </SectionHeaderTitle>

                                    <ClearAction
                                        aria-label={translate('sweep-sheet-clear-cta-a11y')}
                                        // disabled={}
                                        onAction={handleClearCart}
                                    >
                                        {translate('sweep-sheet-clear-cta')}
                                    </ClearAction>
                                </SectionHeader>

                                <CartList>{cartItems}</CartList>
                            </MainInterior>
                        </Sheet.Layout.Columns.Primary>
                    </Sheet.Layout.Columns>

                    <Sheet.Layout.Footer>
                        <FooterInterior>
                            <Summary>
                                <SummaryTitle>{translate('sweep-sheet-summary-title')}</SummaryTitle>
                                <CartPrice currency={currency} price={totalPrice} size={tokens.summary.priceSize} />
                            </Summary>

                            <Button
                                fluid
                                size="medium"
                                variant="solid"
                                aria-label={translate('sweep-sheet-cta-a11y')}
                                // disabled={}
                                // loading={}
                                onClick={handleSubmitOrder}
                            >
                                {translate('sweep-sheet-cta')}
                            </Button>
                        </FooterInterior>
                    </Sheet.Layout.Footer>
                </Sheet.Layout>
            </Sheet.Main>
        </Sheet>
    );
}
